import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ListTodo, Plus, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { hasPermission } from "../../lib/permissions";
import { formatDay, governanceError } from "../../lib/governance";
import { TASK_STATUS_LABELS } from "../../lib/tasks";
import useDirectory from "../../hooks/useDirectory";
import TaskFormSheet from "../TaskFormSheet";
import { Button } from "../ui/button";
import { Card } from "../ui/card";

// The actions of a decision or a meeting are ORDINARY TASKS: created with the usual task form, then linked. `base` is the API path of the owner
// (« /governance/decisions/<id> » or « /governance/meetings/<id> »); `onData` receives the owner's new state after each change.
export default function LinkedActions({ base, tasks, canLink, reference, onData }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const canTask = hasPermission(user, "tasks:create");
  const directory = useDirectory(canTask && hasPermission(user, "tasks:scope_all"));
  const draft = useMemo(() => ({ title: "", domain: "internal_organization", description: `Action issue de ${reference}.` }), [reference]);

  const link = async (task) => {
    setOpen(false);
    try {
      const { data } = await api.post(`${base}/tasks`, { task_id: task.id });
      onData(data);
      toast.success("Action ajoutée");
    } catch (e) {
      toast.error(governanceError(e, "La tâche est créée mais n'a pas pu être reliée."));
    }
  };
  const unlink = async (task) => {
    try {
      const { data } = await api.delete(`${base}/tasks/${task.id}`);
      onData(data);
    } catch (e) {
      toast.error(governanceError(e, "Impossible de retirer cette action."));
    }
  };
  return (
    <Card className="p-4 sm:p-5 space-y-3" data-testid="linked-actions">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-slate-900">Actions</h2>
        {canLink && canTask && <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="action-new"><Plus size={14} className="mr-1.5" />Créer une action</Button>}
      </div>
      {tasks.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="actions-empty">Aucune action pour le moment. Une action est une tâche ordinaire, avec son responsable et son échéance.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {tasks.map((t) => (
            <li key={t.id} className="flex items-center justify-between gap-2 py-2" data-testid={`action-${t.id}`}>
              {t.visible ? (
                <Link to="/tasks" state={{ openTask: t.id }} className="min-w-0 flex items-center gap-2 text-sm text-slate-900 hover:text-jamora">
                  <ListTodo size={15} className="text-slate-400 shrink-0" /><span className="truncate">{t.title}</span>
                  <span className="text-xs text-slate-500 shrink-0">{TASK_STATUS_LABELS[t.status] || t.status}{t.due_date ? ` · ${formatDay(t.due_date)}` : ""}</span>
                </Link>
              ) : <span className="text-sm italic text-slate-500">Action non visible avec vos droits</span>}
              {canLink && <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0" onClick={() => unlink(t)} aria-label="Retirer cette action" data-testid={`action-remove-${t.id}`}><X size={14} /></Button>}
            </li>
          ))}
        </ul>
      )}
      {canTask && <TaskFormSheet open={open} onOpenChange={setOpen} task={null} canScopeAll={hasPermission(user, "tasks:scope_all")} canEdit={false} directory={directory} initialValues={draft} onSaved={link} />}
    </Card>
  );
}
